import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import authService from '@/services/authService'
import type { AuthenticationResultType } from 'aws-sdk/clients/cognitoidentityserviceprovider'
import { queryClient } from '@/shared/services/queryClient'

interface User {
  username: string
  email?: string
  phoneNumber?: string
  name?: string
}

interface AuthState {
  user: User | null
  accessToken: string | null
  idToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Backend user ID (UUID); the profile itself is server state (see useCurrentUser)
  userId: string | null

  // Actions
  setTokens: (tokens: AuthenticationResultType) => void
  setUser: (user: User) => void
  setUserId: (userId: string) => void
  logout: () => void
  refreshTokens: () => Promise<void>
  clearError: () => void
  checkAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      idToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      userId: null,

      setTokens: (tokens: AuthenticationResultType) => {
        set({
          accessToken: tokens.AccessToken || null,
          idToken: tokens.IdToken || null,
          refreshToken: tokens.RefreshToken || get().refreshToken, // Keep existing if not provided
          isAuthenticated: true,
          error: null,
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      setUserId: (userId: string) => {
        set({ userId })
      },

      logout: () => {
        authService.signOut()
        set({
          user: null,
          accessToken: null,
          idToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          userId: null,
        })
        // Drop all cached server data so the next user never sees it
        queryClient.clear()
      },

      refreshTokens: async () => {
        try {
          const tokens = await authService.refreshAccessToken()
          get().setTokens(tokens)
        } catch (error) {
          console.error('Failed to refresh token:', error)
          // On refresh failure, logout the user
          get().logout()
          throw error
        }
      },

      clearError: () => {
        set({ error: null })
      },

      checkAuth: () => {
        let idToken = authService.getIdToken()
        let accessToken = authService.getAccessToken()
        let refreshToken = authService.getRefreshToken()

        // If raw localStorage is empty but Zustand store has tokens
        // (e.g. interceptor cleared localStorage after a stale 401),
        // restore raw localStorage from the store state
        if (!idToken && !accessToken) {
          const currentState = get()
          if (currentState.idToken && currentState.accessToken && currentState.isAuthenticated) {
            localStorage.setItem('idToken', currentState.idToken)
            localStorage.setItem('authToken', currentState.accessToken)
            if (currentState.refreshToken) {
              localStorage.setItem('refreshToken', currentState.refreshToken)
            }
            idToken = currentState.idToken
            accessToken = currentState.accessToken
            refreshToken = currentState.refreshToken
          }
        }

        if (idToken && accessToken) {
          // Try to decode user info from idToken (JWT)
          let user = get().user
          if (!user && idToken) {
            try {
              // Decode JWT payload (middle part between dots)
              const payload = JSON.parse(atob(idToken.split('.')[1]))
              user = {
                username: payload.sub || payload['cognito:username'] || payload.email || payload.phone_number,
                email: payload.email,
                phoneNumber: payload.phone_number,
                name: payload.name,
              }
            } catch (e) {
              console.error('Failed to decode idToken:', e)
            }
          }

          set({
            user,
            idToken,
            accessToken,
            refreshToken,
            isAuthenticated: true,
          })
        } else {
          set({
            user: null,
            accessToken: null,
            idToken: null,
            refreshToken: null,
            isAuthenticated: false,
          })
        }
      },
    }),
    {
      name: 'auth-storage',
      version: 1,
      // v0 persisted the whole profile; keep only its userId
      migrate: (persisted, version) => {
        const state = { ...(persisted as Record<string, unknown>) }
        if (version === 0) {
          const profile = state.userProfile as { userId?: string } | null | undefined
          state.userId = profile?.userId ?? null
          delete state.userProfile
          delete state.isNewUser
          delete state.profileComplete
          delete state.onboardingChecked
        }
        return state
      },
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        idToken: state.idToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        userId: state.userId,
      }),
    }
  )
)
